import { useIonToast } from '@ionic/react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMachineContext } from '../../../pages/machine/machine-context-provider';
import { useRobotManufacturerContext } from '../../../pages/machine/robot-manufacturer-context';
import { BeaconEngineOperations } from '../../../robot/beacon-engine-operations';
import { UROperations } from '../../../robot/manufacturers/universal-robots/ur-operations';
import { RobotManufacturer, RobotOperations } from '../../../robot/robot.types';
import { hasBeaconEngineSupport } from '../../../robot/robot.utils';
import { useCacheWriterBindingLazy } from './cache-writer.binding';
import {
  RobotOperationBindingContext,
  RobotOperationBindingResult,
  isErrorResult,
  useRobotOperationBindingLazy,
} from './robot-operation.binding';
import { useScriptActionBindingLazy } from './script-action.binding';

type SendRobotOperationResolver = (value?: RobotOperationBindingResult) => void;

interface SendRobotOperationMeta {
  resolve: SendRobotOperationResolver;
}

/**
 * Hook that provides robot operations based on the current manufacturer context
 * Abstracts away script execution and provides high-level robot operations
 * Returns properly typed operations based on the manufacturer context
 */
export const useRobotOperations = (): RobotOperations => {
  const { machine } = useMachineContext();
  const manufacturerContext = useRobotManufacturerContext();
  const { manufacturer } = manufacturerContext;
  const isBeaconEngine = hasBeaconEngineSupport(manufacturerContext);
  const { i18n } = useTranslation();
  const [presentToast] = useIonToast();

  // One-shot binding for operations that execute once and complete
  // (moveToPoint, halt, stopProgram)
  const { send: sendScriptOneShot } = useScriptActionBindingLazy({
    closeImmediately: true,
  });

  // Long-running binding for operations that need to stay active
  // (startFineTune - keeps binding open for subsequent fineTuneMoveTo calls)
  const { send: sendScriptLongRunning, close: closeScriptBinding } =
    useScriptActionBindingLazy({
      closeImmediately: false,
    });

  const writeToCache = useCacheWriterBindingLazy();

  const sendRobotResolversRef = useRef(new Set<SendRobotOperationResolver>());

  useEffect(() => {
    const sendRobotResolvers = sendRobotResolversRef.current;
    return () => {
      // Clean up any pending resolvers on unmount
      sendRobotResolvers.forEach((resolve) => resolve());
    };
  }, []);

  const { send: sendRobotOperation, close: closeRobotOperation } =
    useRobotOperationBindingLazy<SendRobotOperationMeta>({
      closeImmediately: false,
      onResult: (result, meta) => {
        // Automatically close the binding when the operation is done
        if (result?.done) {
          closeRobotOperation();
          if (meta) {
            meta.resolve(result);
            sendRobotResolversRef.current.delete(meta.resolve);
          }
          if (isErrorResult(result)) {
            presentToast({
              message: result.errorMessage,
              color: 'danger',
              position: 'top',
              buttons: [
                {
                  role: 'cancel',
                  text: i18n.t('common:close'),
                },
              ],
            });
          }
        }
      },
    });

  const awaitSendRobotOperationDone = useMemo(() => {
    return async (
      machineId: string,
      context: RobotOperationBindingContext
    ): Promise<RobotOperationBindingResult | undefined> => {
      let resolver: SendRobotOperationResolver | undefined;

      const promise = new Promise<RobotOperationBindingResult | undefined>(
        (resolve) => {
          resolver = resolve;
          sendRobotResolversRef.current.add(resolver);
        }
      );

      const noop = () => {};
      const meta: SendRobotOperationMeta = {
        // Technically, resolver will be defined at this point
        // but TypeScript can't infer that, so providing a default
        // function to satisfy the type checker, which is safer
        // I suppose than bypassing it with `!`
        resolve: resolver ?? noop,
      };

      sendRobotOperation(machineId, context, meta);

      // Don't resolve until we receive the binding result
      // indicating that the operation is done
      return promise;
    };
  }, [sendRobotOperation]);

  // Initialize cache writer binding
  useEffect(() => {
    writeToCache(machine?.id, {
      requests: [],
    });
  }, [writeToCache, machine?.id]);

  const operations = useMemo(() => {
    if (isBeaconEngine) {
      return new BeaconEngineOperations(
        writeToCache,
        awaitSendRobotOperationDone
      );
    }

    switch (manufacturer) {
      case RobotManufacturer.UNIVERSAL_ROBOTS:
        return new UROperations(
          sendScriptOneShot,
          sendScriptLongRunning,
          closeScriptBinding,
          writeToCache
        );
      default:
        throw new Error(
          `Operations not implemented for manufacturer: ${manufacturer}`
        );
    }
  }, [
    isBeaconEngine,
    manufacturer,
    writeToCache,
    awaitSendRobotOperationDone,
    sendScriptOneShot,
    sendScriptLongRunning,
    closeScriptBinding,
  ]);

  return operations;
};

/**
 * Hook that provides lazy robot operations initialization
 * Useful when you don't need operations immediately on mount
 * Returns a factory function that creates properly typed operations
 */
export const useRobotOperationsLazy = () => {
  const { machine } = useMachineContext();
  const manufacturerContext = useRobotManufacturerContext();
  const { manufacturer } = manufacturerContext;
  const isBeaconEngine = hasBeaconEngineSupport(manufacturerContext);
  const { i18n } = useTranslation();
  const [presentToast] = useIonToast();

  // One-shot binding for operations that execute once and complete
  // (moveToPoint, halt, stopProgram)
  const { send: sendScriptOneShot } = useScriptActionBindingLazy({
    closeImmediately: true,
  });

  // Long-running binding for operations that need to stay active
  // (startFineTune - keeps binding open for subsequent fineTuneMoveTo calls)
  const { send: sendScriptLongRunning, close: closeScriptBinding } =
    useScriptActionBindingLazy({
      closeImmediately: false,
    });

  const writeToCache = useCacheWriterBindingLazy();

  const sendRobotResolversRef = useRef(new Set<SendRobotOperationResolver>());

  useEffect(() => {
    const sendRobotResolvers = sendRobotResolversRef.current;
    return () => {
      // Clean up any pending resolvers on unmount
      sendRobotResolvers.forEach((resolve) => resolve());
    };
  }, []);

  const { send: sendRobotOperation, close: closeRobotOperation } =
    useRobotOperationBindingLazy<SendRobotOperationMeta>({
      closeImmediately: false,
      onResult: (result, meta) => {
        // Automatically close the binding when the operation is done
        if (result?.done) {
          closeRobotOperation();
          if (meta) {
            meta.resolve(result);
            sendRobotResolversRef.current.delete(meta.resolve);
          }
          if (isErrorResult(result)) {
            presentToast({
              message: result.errorMessage,
              color: 'danger',
              position: 'top',
              buttons: [
                {
                  role: 'cancel',
                  text: i18n.t('common:close'),
                },
              ],
            });
          }
        }
      },
    });

  const awaitSendRobotOperationDone = useMemo(() => {
    return async (
      machineId: string,
      context: RobotOperationBindingContext
    ): Promise<RobotOperationBindingResult | undefined> => {
      let resolver: SendRobotOperationResolver | undefined;

      const promise = new Promise<RobotOperationBindingResult | undefined>(
        (resolve) => {
          resolver = resolve;
          sendRobotResolversRef.current.add(resolver);
        }
      );

      const noop = () => {};
      const meta: SendRobotOperationMeta = {
        // Technically, resolver will be defined at this point
        // but TypeScript can't infer that, so providing a default
        // function to satisfy the type checker, which is safer
        // I suppose than bypassing it with `!`
        resolve: resolver ?? noop,
      };

      sendRobotOperation(machineId, context, meta);

      // Don't resolve until we receive the binding result
      // indicating that the operation is done
      return promise;
    };
  }, [sendRobotOperation]);

  // Initialize cache writer binding
  useEffect(() => {
    writeToCache(machine?.id, {
      requests: [],
    });
  }, [writeToCache, machine?.id]);

  const createOperations = useMemo(() => {
    return (): RobotOperations => {
      if (isBeaconEngine) {
        return new BeaconEngineOperations(
          writeToCache,
          awaitSendRobotOperationDone
        );
      }

      switch (manufacturer) {
        case RobotManufacturer.UNIVERSAL_ROBOTS:
          return new UROperations(
            sendScriptOneShot,
            sendScriptLongRunning,
            closeScriptBinding,
            writeToCache
          );
        default:
          throw new Error(
            `Operations not implemented for manufacturer: ${manufacturer}`
          );
      }
    };
  }, [
    isBeaconEngine,
    manufacturer,
    writeToCache,
    awaitSendRobotOperationDone,
    sendScriptOneShot,
    sendScriptLongRunning,
    closeScriptBinding,
  ]);

  return createOperations;
};
